import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Send } from 'lucide-react'
import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'
import { saveDraft, loadDraft, clearDraft } from '../../utilities/messageHelpers'

function MessageInput({ conversationId, onSendMessage, disabled = false }) {
  const [message, setMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const textareaRef = useRef(null)

  useEffect(() => {
    if (conversationId) {
      const draft = loadDraft(conversationId)
      setMessage(draft)
    }
  }, [conversationId])

  useEffect(() => {
    if (conversationId && message) {
      saveDraft(conversationId, message)
    }
  }, [conversationId, message])

  const handleSend = async () => {
    if (!message.trim() || isSending) return

    setIsSending(true)
    try {
      await onSendMessage(message.trim())
      setMessage('')
      if (conversationId) {
        clearDraft(conversationId)
      }
      textareaRef.current?.focus()
    } catch {
    } finally {
      setIsSending(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className="flex items-end gap-2 p-4 border-t border-gray-200 bg-white">
      <Textarea
        ref={textareaRef}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Type a message..."
        disabled={disabled || isSending}
        className="min-h-[44px] max-h-[120px] resize-none"
        rows={1}
      />
      <Button
        onClick={handleSend}
        disabled={!message.trim() || disabled || isSending}
        size="icon"
        className="flex-shrink-0 h-11 w-11 bg-purple-600 hover:bg-purple-700"
      >
        <Send className="w-5 h-5" />
      </Button>
    </div>
  )
}

MessageInput.propTypes = {
  conversationId: PropTypes.string,
  onSendMessage: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}

export default MessageInput
